import { usePermissions } from '@hetivo/hetivo-lib-front-utils';
import type { MultiSelectOption } from '@hetivo/hetivo-lib-web-utils';

import { useLocationsForSelectQuery } from '&/features/settings/locations/hooks';
import type { UseLocationsForSelectOptions } from '&/features/settings/locations/hooks';
import { useMasterContext } from '&/master';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InspectionLocationOptions {
  /**
   * True once the options are known, so a chosen location that is not among them can be cleared. Also true when the
   * caller may not list locations: the filter then offers nothing, and nothing chosen can stay.
   */
  isLoaded: boolean;
  options: MultiSelectOption[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** The grant the Inspector role holds for this filter (`built-in.ts`: "feeds the inspection list's location filter"). */
const LOCATIONS_LIST_PERMISSION = 'locations:list';

/** One stable empty list, so a caller's effect does not re-run on every render. */
const NO_OPTIONS: MultiSelectOption[] = [];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Which locations to ask for, by master selector level (research R8): the selected location alone, every location of
 * the selected territory, or, with the tenant selected, every location the caller may see (the route is scoped to
 * the caller's own locations server-side).
 */
function locationQueryFor(siteId: string | null, territoryId: string | null): UseLocationsForSelectOptions {
  if (siteId !== null) {
    return { ids: [siteId] };
  }
  if (territoryId !== null) {
    return { territoryIds: [territoryId] };
  }
  return {};
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * The locations the inspections page's Location filter offers (SWOOP-401, FR-005): only those inside both the
 * caller's role scope (the route answers no other) and the master selector's choice. Active locations only.
 */
export function useInspectionLocationOptions(): InspectionLocationOptions {
  const { hasPermission } = usePermissions();
  const { siteId, territoryId } = useMasterContext();
  const enabled = hasPermission(LOCATIONS_LIST_PERMISSION);

  const { isLoaded, options } = useLocationsForSelectQuery({ ...locationQueryFor(siteId, territoryId), enabled });

  return enabled ? { isLoaded, options } : { isLoaded: true, options: NO_OPTIONS };
}
